/**
 * Versioned HTTP contract — tracker `v1` — for the tracker api
 * (`services/tracker-api`, mounted same-origin at `/api/tracker/v1/*`).
 *
 * Same rules as `./v1.js`: both sides parse the SAME Zod shapes (ADR 0005); the
 * `{data, error, meta}` envelope and error kinds are reused verbatim; breaking
 * changes ship as `tracker-v2` alongside, never by mutating these.
 *
 * Owner scoping: like the ideas persistence API pre-DEVOP-4, the client supplies
 * `ownerId` and the server scopes every row by it. Deriving the owner from the
 * verified Access JWT replaces this at DEVOP-4 (documented stopgap).
 */
import { z } from 'zod';
import {
  ActivityEntrySchema,
  CommentSchema,
  NotificationSchema,
  CycleSchema,
  SyncDirectionSchema,
  SyncStatusSchema,
  TrackerIssueSchema,
  TrackerProjectSchema,
  WorkspaceSnapshotSchema,
} from '../tracker/domain.js';

export const TRACKER_HTTP_CONTRACT_VERSION = 'tracker-v1' as const;

const ownerId = z.string().min(1);

/** GET /api/tracker/v1/workspace?ownerId=… → snapshot (null data = nothing stored yet). */
export const TrackerWorkspaceResponseSchema = WorkspaceSnapshotSchema.nullable();

/** PUT /api/tracker/v1/workspace — bootstrap/replace the owner's snapshot (first-run seed). */
export const TrackerWorkspacePutRequestSchema = z.object({
  ownerId,
  snapshot: WorkspaceSnapshotSchema,
});

/** PUT /api/tracker/v1/issues — upsert one issue + the activity it generated. */
export const TrackerIssuePutRequestSchema = z.object({
  ownerId,
  issue: TrackerIssueSchema,
  activity: z.array(ActivityEntrySchema),
  /** Team counter after allocation, so identifiers stay monotonic server-side. */
  teamIssueCounter: z.number().int().nonnegative(),
});

/** POST /api/tracker/v1/comments — append a comment. */
export const TrackerCommentPostRequestSchema = z.object({
  ownerId,
  comment: CommentSchema,
});

/** PUT /api/tracker/v1/projects — upsert a project. */
export const TrackerProjectPutRequestSchema = z.object({
  ownerId,
  project: TrackerProjectSchema,
});

/** PUT /api/tracker/v1/cycles — replace a team's cycles (rollover writes several). */
export const TrackerCyclesPutRequestSchema = z.object({
  ownerId,
  teamId: z.string().min(1),
  cycles: z.array(CycleSchema),
});

/** PUT /api/tracker/v1/notifications — replace the owner's notification list (inbox state). */
export const TrackerNotificationsPutRequestSchema = z.object({
  ownerId,
  notifications: z.array(NotificationSchema),
});

/* ── Linear sync (ADR 0070/0071) ──────────────────────────────────────────── */

/** POST /api/tracker/v1/sync/pull — run a pull reconcile for the owner. */
export const TrackerSyncPullRequestSchema = z.object({ ownerId });

/** POST /api/tracker/v1/sync/push — push one issue (two-way). */
export const TrackerSyncPushRequestSchema = z.object({ ownerId, issueId: z.string().min(1) });

/** GET /api/tracker/v1/sync/status?ownerId=… → SyncStatus. */
export const TrackerSyncStatusResponseSchema = SyncStatusSchema;

/** PUT /api/tracker/v1/sync/config — set the runtime direction. */
export const TrackerSyncConfigPutRequestSchema = z.object({ ownerId, direction: SyncDirectionSchema });

/** The reconcile result surfaced to the client (a run summary). */
export const TrackerSyncResultSchema = SyncStatusSchema;

/** Generic acknowledge payload for write endpoints. */
export const TrackerAckSchema = z.object({ ok: z.literal(true) });

export type TrackerWorkspacePutRequest = z.infer<typeof TrackerWorkspacePutRequestSchema>;
export type TrackerIssuePutRequest = z.infer<typeof TrackerIssuePutRequestSchema>;
export type TrackerCommentPostRequest = z.infer<typeof TrackerCommentPostRequestSchema>;
export type TrackerProjectPutRequest = z.infer<typeof TrackerProjectPutRequestSchema>;
export type TrackerCyclesPutRequest = z.infer<typeof TrackerCyclesPutRequestSchema>;
export type TrackerNotificationsPutRequest = z.infer<typeof TrackerNotificationsPutRequestSchema>;
export type TrackerSyncPullRequest = z.infer<typeof TrackerSyncPullRequestSchema>;
export type TrackerSyncPushRequest = z.infer<typeof TrackerSyncPushRequestSchema>;
export type TrackerSyncConfigPutRequest = z.infer<typeof TrackerSyncConfigPutRequestSchema>;
